import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Loader2, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const CheckboxGroup = ({ options, value = [], onChange, columns = 2 }) => (
  <div className={`grid grid-cols-${columns} gap-2`}>
    {options.map((opt) => {
      const val = typeof opt === "string" ? opt : opt.value;
      const label = typeof opt === "string" ? opt : opt.label;
      const checked = value.includes(val);
      return (
        <button key={val} type="button" onClick={() => onChange(checked ? value.filter(v => v !== val) : [...value, val])}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${checked ? "bg-[#f7c935]/10 border-[#f7c935]/40 text-white" : "bg-[#0b0811] border-white/10 text-slate-400 hover:border-white/20"}`}
          data-testid={`cb-${val.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${checked ? "bg-[#f7c935] border-[#f7c935]" : "border-white/20"}`}>
            {checked && <CheckCircle2 className="w-3 h-3 text-[#14101c]" />}
          </div>
          {label}
        </button>
      );
    })}
  </div>
);

const RadioGroup = ({ options, value, onChange, columns = 2 }) => (
  <div className={`grid grid-cols-${columns} gap-2`}>
    {options.map((opt) => {
      const val = typeof opt === "string" ? opt : opt.value;
      const label = typeof opt === "string" ? opt : opt.label;
      return (
        <button key={val} type="button" onClick={() => onChange(val)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${value === val ? "bg-[#f7c935]/10 border-[#f7c935]/40 text-white" : "bg-[#0b0811] border-white/10 text-slate-400 hover:border-white/20"}`}>
          <div className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center ${value === val ? "border-[#f7c935]" : "border-white/20"}`}>
            {value === val && <div className="w-2 h-2 rounded-full bg-[#f7c935]" />}
          </div>
          {label}
        </button>
      );
    })}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-medium text-slate-400 mb-1.5 block">{label}</label>
    {children}
  </div>
);

const inputCls = "bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935] h-10";
const textCls = "bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]";

const Section = ({ id, title, number, children, sections, toggle }) => {
  const open = sections[id] !== false;
  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden animate-fade-in-up">
      <button onClick={() => toggle(id)} className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black" style={{ background: "#f7c935", color: "#14101c" }}>{number}</span>
          <h2 className="text-lg font-bold">{title}</h2>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
      </button>
      {open && <div className="px-5 pb-6 space-y-5 border-t border-white/5 pt-5">{children}</div>}
    </div>
  );
};

export default function FicheRenseignementPage() {
  const { userId } = useParams();
  const { user, axiosAuth, isAdmin } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sections, setSections] = useState({});

  const targetUserId = userId || user?.user_id;

  useEffect(() => {
    api.get(`/fiche-renseignement/${targetUserId}`).then(r => {
      if (r.data && Object.keys(r.data).length > 0) setForm(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [targetUserId]);

  const f = (key) => form[key] || "";
  const fArr = (key) => form[key] || [];
  const set = (key, val) => setForm({ ...form, [key]: val });
  const toggle = (section) => setSections({ ...sections, [section]: !sections[section] });

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.post("/fiche-renseignement", { ...form, client_id: targetUserId });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="fiche-renseignement-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tighter">Fiche de <span style={{ color: "#f7c935" }}>Renseignement</span></h1>
          <p className="text-slate-400 mt-2">Remplissez ce formulaire pour que Blitzz puisse analyser votre situation</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="save-fiche-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : saved ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {saved ? "Enregistre !" : "Enregistrer"}
        </Button>
      </div>

      <div className="space-y-4">
        {/* 1. Informations Generales */}
        <Section id="general" title="Informations Generales de l'Entreprise" number="1" sections={sections} toggle={toggle}>
          <h3 className="text-sm font-bold" style={{ color: "#f7c935" }}>Identification</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Raison sociale"><Input value={f("raison_sociale")} onChange={(e) => set("raison_sociale", e.target.value)} className={inputCls} data-testid="fiche-raison-sociale" /></Field>
            <Field label="Forme juridique">
              <RadioGroup value={f("forme_juridique")} onChange={(v) => set("forme_juridique", v)} options={["SARL", "SAS", "EURL", "SA", "Auto-entrepreneur", "Autre"]} columns={3} />
            </Field>
            {f("forme_juridique") === "Autre" && <Field label="Preciser"><Input value={f("forme_juridique_autre")} onChange={(e) => set("forme_juridique_autre", e.target.value)} className={inputCls} /></Field>}
            <Field label="SIRET"><Input value={f("siret")} onChange={(e) => set("siret", e.target.value)} className={inputCls} /></Field>
            <Field label="Code APE/NAF"><Input value={f("code_ape")} onChange={(e) => set("code_ape", e.target.value)} className={inputCls} /></Field>
            <Field label="Date de creation"><Input type="date" value={f("date_creation")} onChange={(e) => set("date_creation", e.target.value)} className={inputCls} /></Field>
          </div>
          <h3 className="text-sm font-bold mt-6" style={{ color: "#f7c935" }}>Coordonnees</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Field label="Adresse du siege social"><Input value={f("adresse")} onChange={(e) => set("adresse", e.target.value)} className={inputCls} /></Field></div>
            <Field label="Code postal"><Input value={f("code_postal")} onChange={(e) => set("code_postal", e.target.value)} className={inputCls} /></Field>
            <Field label="Ville"><Input value={f("ville")} onChange={(e) => set("ville", e.target.value)} className={inputCls} /></Field>
            <Field label="Site web"><Input value={f("site_web")} onChange={(e) => set("site_web", e.target.value)} className={inputCls} /></Field>
            <Field label="Email principal"><Input value={f("email_principal")} onChange={(e) => set("email_principal", e.target.value)} className={inputCls} /></Field>
            <Field label="Telephone"><Input value={f("telephone")} onChange={(e) => set("telephone", e.target.value)} className={inputCls} /></Field>
          </div>
          <h3 className="text-sm font-bold mt-6" style={{ color: "#f7c935" }}>Dirigeant Principal</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nom et Prenom"><Input value={f("dirigeant_nom")} onChange={(e) => set("dirigeant_nom", e.target.value)} className={inputCls} /></Field>
            <Field label="Fonction"><Input value={f("dirigeant_fonction")} onChange={(e) => set("dirigeant_fonction", e.target.value)} className={inputCls} /></Field>
            <Field label="Email direct"><Input value={f("dirigeant_email")} onChange={(e) => set("dirigeant_email", e.target.value)} className={inputCls} /></Field>
            <Field label="Telephone direct"><Input value={f("dirigeant_tel")} onChange={(e) => set("dirigeant_tel", e.target.value)} className={inputCls} /></Field>
          </div>
        </Section>

        {/* 2. Profil */}
        <Section id="profil" title="Profil de l'Entreprise" number="2" sections={sections} toggle={toggle}>
          <h3 className="text-sm font-bold" style={{ color: "#f7c935" }}>Activite</h3>
          <Field label="Secteur d'activite">
            <RadioGroup value={f("secteur")} onChange={(v) => set("secteur", v)} options={["Commerce", "Services", "BTP", "Industrie", "Digital", "Sante", "Autre"]} columns={4} />
          </Field>
          <Field label="Description de l'activite principale"><Textarea value={f("activite_description")} onChange={(e) => set("activite_description", e.target.value)} rows={3} className={textCls} /></Field>
          <Field label="Produits/Services principaux"><Textarea value={f("produits_services")} onChange={(e) => set("produits_services", e.target.value)} rows={2} className={textCls} /></Field>

          <h3 className="text-sm font-bold mt-6" style={{ color: "#f7c935" }}>Structure</h3>
          <Field label="Effectif total">
            <RadioGroup value={f("effectif")} onChange={(v) => set("effectif", v)} options={["1-5", "6-10", "11-20", "21-50", "50+"]} columns={5} />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nombre de cadres"><Input value={f("nb_cadres")} onChange={(e) => set("nb_cadres", e.target.value)} className={inputCls} /></Field>
            <Field label="Nombre de commerciaux"><Input value={f("nb_commerciaux")} onChange={(e) => set("nb_commerciaux", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Organisation actuelle">
            <RadioGroup value={f("organisation")} onChange={(v) => set("organisation", v)} options={[
              "Dirigeant seul", "Equipe operationnelle sans hierarchie", "Structure avec managers intermediaires", "Organisation matricielle"
            ]} columns={2} />
          </Field>
        </Section>

        {/* 3. Situation Financiere */}
        <Section id="finance" title="Situation Financiere" number="3" sections={sections} toggle={toggle}>
          <h3 className="text-sm font-bold" style={{ color: "#f7c935" }}>Chiffre d'Affaires</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="CA annee N-2"><Input value={f("ca_n2")} onChange={(e) => set("ca_n2", e.target.value)} placeholder="EUR" className={inputCls} /></Field>
            <Field label="CA annee N-1"><Input value={f("ca_n1")} onChange={(e) => set("ca_n1", e.target.value)} placeholder="EUR" className={inputCls} /></Field>
            <Field label="CA previsionnel annee N"><Input value={f("ca_prev")} onChange={(e) => set("ca_prev", e.target.value)} placeholder="EUR" className={inputCls} /></Field>
          </div>
          <Field label="Evolution">
            <RadioGroup value={f("evolution_ca")} onChange={(v) => set("evolution_ca", v)} options={["En croissance", "Stable", "En baisse"]} columns={3} />
          </Field>

          <h3 className="text-sm font-bold mt-6" style={{ color: "#f7c935" }}>Rentabilite</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Marge brute moyenne (%)"><Input value={f("marge_brute")} onChange={(e) => set("marge_brute", e.target.value)} className={inputCls} /></Field>
            <Field label="Resultat net N-1 (EUR)"><Input value={f("resultat_net")} onChange={(e) => set("resultat_net", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Resultat">
            <RadioGroup value={f("resultat_type")} onChange={(v) => set("resultat_type", v)} options={["Benefice", "Perte"]} columns={2} />
          </Field>

          <Field label="Tresorerie actuelle">
            <RadioGroup value={f("tresorerie")} onChange={(v) => set("tresorerie", v)} options={["Confortable (>3 mois)", "Correcte (1-3 mois)", "Tendue (<1 mois)", "Critique"]} columns={2} />
          </Field>

          <h3 className="text-sm font-bold mt-6" style={{ color: "#f7c935" }}>Financements</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Dettes bancaires en cours (EUR)"><Input value={f("dettes_bancaires")} onChange={(e) => set("dettes_bancaires", e.target.value)} className={inputCls} /></Field>
            <Field label="Decouvert autorise (EUR)"><Input value={f("decouvert")} onChange={(e) => set("decouvert", e.target.value)} className={inputCls} /></Field>
            <Field label="Dettes fournisseurs (EUR)"><Input value={f("dettes_fournisseurs")} onChange={(e) => set("dettes_fournisseurs", e.target.value)} className={inputCls} /></Field>
            <Field label="Dettes sociales/fiscales (EUR)"><Input value={f("dettes_sociales")} onChange={(e) => set("dettes_sociales", e.target.value)} className={inputCls} /></Field>
          </div>
        </Section>

        {/* 4. Diagnostic Commercial */}
        <Section id="commercial" title="Diagnostic Commercial" number="4" sections={sections} toggle={toggle}>
          <h3 className="text-sm font-bold" style={{ color: "#f7c935" }}>Clients</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Nombre de clients actifs"><Input value={f("nb_clients")} onChange={(e) => set("nb_clients", e.target.value)} className={inputCls} /></Field>
            <Field label="Panier moyen (EUR)"><Input value={f("panier_moyen")} onChange={(e) => set("panier_moyen", e.target.value)} className={inputCls} /></Field>
            <Field label="Taux de fidelisation (%)"><Input value={f("taux_fidelisation")} onChange={(e) => set("taux_fidelisation", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Typologie de clientele">
            <RadioGroup value={f("type_clientele")} onChange={(v) => set("type_clientele", v)} options={["B2B", "B2C", "Mixte"]} columns={3} />
          </Field>
          <Field label="Dependance client">
            <RadioGroup value={f("dependance_client")} onChange={(v) => set("dependance_client", v)} options={["Aucune", "Faible", "Moyenne", "Forte (>30% du CA sur 1 client)"]} columns={2} />
          </Field>

          <h3 className="text-sm font-bold mt-6" style={{ color: "#f7c935" }}>Processus Commercial</h3>
          <Field label="Canaux d'acquisition actuels">
            <CheckboxGroup value={fArr("canaux_acquisition")} onChange={(v) => set("canaux_acquisition", v)} options={[
              "Prospection telephonique", "Reseau/Recommandations", "Site internet", "Reseaux sociaux",
              "Publicite en ligne", "Salons professionnels", "Prescripteurs", "Autre"
            ]} columns={2} />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Taux de transformation (%)"><Input value={f("taux_transformation")} onChange={(e) => set("taux_transformation", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Cycle de vente moyen">
            <RadioGroup value={f("cycle_vente")} onChange={(v) => set("cycle_vente", v)} options={["<1 semaine", "1-4 semaines", "1-3 mois", ">3 mois"]} columns={4} />
          </Field>
          <Field label="Outils commerciaux utilises">
            <CheckboxGroup value={fArr("outils_commerciaux")} onChange={(v) => set("outils_commerciaux", v)} options={["CRM", "Tableaux Excel", "Aucun outil structure"]} columns={3} />
          </Field>
        </Section>

        {/* 5. Diagnostic Organisationnel */}
        <Section id="orga" title="Diagnostic Organisationnel" number="5" sections={sections} toggle={toggle}>
          <h3 className="text-sm font-bold" style={{ color: "#f7c935" }}>Processus Internes</h3>
          <Field label="Niveau de digitalisation">
            <RadioGroup value={f("digitalisation")} onChange={(v) => set("digitalisation", v)} options={["Faible", "Moyen", "Avance"]} columns={3} />
          </Field>
          <Field label="Formalisation des processus">
            <RadioGroup value={f("formalisation")} onChange={(v) => set("formalisation", v)} options={[
              "Aucun processus documente", "Quelques procedures informelles", "Processus cles documentes", "Organisation ISO ou equivalent"
            ]} columns={2} />
          </Field>

          <h3 className="text-sm font-bold mt-6" style={{ color: "#f7c935" }}>Climat Social</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Turnover annuel (%)"><Input value={f("turnover")} onChange={(e) => set("turnover", e.target.value)} className={inputCls} /></Field>
            <Field label="Anciennete moyenne (ans)"><Input value={f("anciennete_moyenne")} onChange={(e) => set("anciennete_moyenne", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Ambiance generale">
            <RadioGroup value={f("ambiance")} onChange={(v) => set("ambiance", v)} options={["Excellente", "Bonne", "Correcte", "Tendue", "Conflictuelle"]} columns={5} />
          </Field>
          <Field label="Elements de tension identifies"><Textarea value={f("tensions")} onChange={(e) => set("tensions", e.target.value)} rows={2} className={textCls} /></Field>
        </Section>

        {/* 6. Problematiques et Besoins */}
        <Section id="besoins" title="Problematiques et Besoins" number="6" sections={sections} toggle={toggle}>
          <Field label="Situation actuelle">
            <RadioGroup value={f("situation_actuelle")} onChange={(v) => set("situation_actuelle", v)} options={[
              "Developpement & Croissance", "Relance et Coup de Boost", "Urgence & Sauvetage"
            ]} columns={3} />
          </Field>
          <Field label="Difficultes rencontrees">
            <CheckboxGroup value={fArr("difficultes")} onChange={(v) => set("difficultes", v)} options={[
              "Difficultes financieres", "Tresorerie critique", "Rentabilite insuffisante", "Dettes importantes",
              "Risque de cessation de paiement", "Prospection inefficace", "Taux de transformation faible",
              "Perte de clients importants", "Concurrence agressive", "Processus desorganises",
              "Manque de coordination", "Systeme d'information inadapte", "Charge administrative excessive",
              "Manque de competences", "Problemes de management", "Turnover eleve", "Demotivation generale"
            ]} columns={2} />
          </Field>
          <Field label="Objectifs prioritaires (3 maximum)"><Textarea value={f("objectifs_prioritaires")} onChange={(e) => set("objectifs_prioritaires", e.target.value)} rows={3} placeholder="1. &#10;2. &#10;3. " className={textCls} /></Field>
          <h3 className="text-sm font-bold mt-4" style={{ color: "#f7c935" }}>Resultats Attendus</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="A 3 mois"><Textarea value={f("resultats_3m")} onChange={(e) => set("resultats_3m", e.target.value)} rows={2} className={textCls} /></Field>
            <Field label="A 6 mois"><Textarea value={f("resultats_6m")} onChange={(e) => set("resultats_6m", e.target.value)} rows={2} className={textCls} /></Field>
            <Field label="A 9 mois"><Textarea value={f("resultats_9m")} onChange={(e) => set("resultats_9m", e.target.value)} rows={2} className={textCls} /></Field>
          </div>
        </Section>

        {/* 7. Historique */}
        <Section id="historique" title="Historique d'Accompagnement" number="7" sections={sections} toggle={toggle}>
          <Field label="Avez-vous deja ete accompagne par un consultant ?">
            <RadioGroup value={f("accompagne_avant")} onChange={(v) => set("accompagne_avant", v)} options={["Oui", "Non"]} columns={2} />
          </Field>
          {f("accompagne_avant") === "Oui" && (
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Cabinet"><Input value={f("cabinet_precedent")} onChange={(e) => set("cabinet_precedent", e.target.value)} className={inputCls} /></Field>
              <Field label="Periode"><Input value={f("periode_precedent")} onChange={(e) => set("periode_precedent", e.target.value)} className={inputCls} /></Field>
              <div className="md:col-span-2"><Field label="Resultats obtenus"><Textarea value={f("resultats_precedent")} onChange={(e) => set("resultats_precedent", e.target.value)} rows={2} className={textCls} /></Field></div>
            </div>
          )}
        </Section>

        {/* 8. Capacite d'Investissement */}
        <Section id="investissement" title="Capacite d'Investissement" number="8" sections={sections} toggle={toggle}>
          <Field label="Budget mensuel envisage">
            <RadioGroup value={f("budget_mensuel")} onChange={(v) => set("budget_mensuel", v)} options={[
              "2 000 - 3 000 EUR HT/mois", "3 000 - 5 000 EUR HT/mois", "> 5 000 EUR HT/mois", "Formule BLITZ (remuneration aux resultats)"
            ]} columns={2} />
          </Field>
          <Field label="Capacite d'engagement">
            <RadioGroup value={f("engagement")} onChange={(v) => set("engagement", v)} options={[
              "1-3 mois (cycle court)", "3-6 mois (cycle moyen)", "6-9 mois (cycle complet)"
            ]} columns={3} />
          </Field>

          <h3 className="text-sm font-bold mt-6" style={{ color: "#f7c935" }}>Services Complementaires Souhaites</h3>
          <Field label="Digital & Web">
            <CheckboxGroup value={fArr("services_digital")} onChange={(v) => set("services_digital", v)} options={[
              { value: "C01", label: "Creation site vitrine (1 500 EUR)" },
              { value: "C02", label: "Site e-commerce (2 500 EUR)" },
              { value: "C03", label: "Developpement Full-Stack (1 500 EUR)" },
            ]} columns={1} />
          </Field>
          <Field label="Strategie Commerciale & Communication">
            <CheckboxGroup value={fArr("services_commercial")} onChange={(v) => set("services_commercial", v)} options={[
              { value: "CM1", label: "Negociation commerciale (600 EUR)" },
              { value: "CM2", label: "Automatisation marketing (2 000 EUR)" },
              { value: "CM3", label: "Image de marque (1 500 EUR)" },
            ]} columns={1} />
          </Field>
          <Field label="Gestion Sociale & Comptable">
            <CheckboxGroup value={fArr("services_gestion")} onChange={(v) => set("services_gestion", v)} options={[
              { value: "CS1", label: "Gestion de paie (200 EUR/mois)" },
              { value: "CS2", label: "Comptabilite (200 EUR/mois)" },
              { value: "CS3", label: "Analyse financiere (1 000 EUR)" },
            ]} columns={1} />
          </Field>
          <Field label="Formations Qualiopi">
            <CheckboxGroup value={fArr("formations_souhaitees")} onChange={(v) => set("formations_souhaitees", v)} options={[
              { value: "P1", label: "Developpement d'entreprise (3 000 EUR)" },
              { value: "P2", label: "Analyse comptable (2 000 EUR)" },
              { value: "P3", label: "Projet entrepreneurial (1 500 EUR)" },
              { value: "P4", label: "Gestion du stress (1 500 EUR)" },
              { value: "P5", label: "Techniques de vente (1 500 EUR)" },
            ]} columns={1} />
          </Field>
        </Section>

        {/* 9. Engagement */}
        <Section id="engagement_section" title="Engagement et Disponibilite" number="9" sections={sections} toggle={toggle}>
          <Field label="Disponibilite hebdomadaire pour l'accompagnement">
            <RadioGroup value={f("disponibilite")} onChange={(v) => set("disponibilite", v)} options={["<2h", "2-5h", "5-10h", ">10h"]} columns={4} />
          </Field>
          <Field label="Volonte de changement">
            <RadioGroup value={f("volonte_changement")} onChange={(v) => set("volonte_changement", v)} options={["Faible", "Moyenne", "Forte", "Totale"]} columns={4} />
          </Field>
          <Field label="Niveau d'ouverture aux recommandations">
            <RadioGroup value={f("ouverture")} onChange={(v) => set("ouverture", v)} options={["Tres ouvert", "Ouvert avec reserves", "Prudent", "Besoin de preuves tangibles"]} columns={2} />
          </Field>
          <Field label="Delai souhaite pour demarrage">
            <RadioGroup value={f("delai_demarrage")} onChange={(v) => set("delai_demarrage", v)} options={["Immediat", "<2 semaines", "<1 mois", "Flexible"]} columns={4} />
          </Field>
          <Field label="Niveau d'urgence">
            <RadioGroup value={f("urgence")} onChange={(v) => set("urgence", v)} options={["Critique", "Eleve", "Modere", "Faible"]} columns={4} />
          </Field>
        </Section>

        {/* 10. Informations Complementaires */}
        <Section id="complement" title="Informations Complementaires" number="10" sections={sections} toggle={toggle}>
          <Field label="Elements specifiques a prendre en compte"><Textarea value={f("elements_specifiques")} onChange={(e) => set("elements_specifiques", e.target.value)} rows={3} className={textCls} /></Field>
          <Field label="Attentes particulieres vis-a-vis de Blitzz"><Textarea value={f("attentes_blitzz")} onChange={(e) => set("attentes_blitzz", e.target.value)} rows={3} className={textCls} /></Field>
        </Section>

        {/* 11. Evaluation Blitzz (admin only) */}
        {isAdmin && (
          <Section id="evaluation" title="Evaluation Preliminaire (Blitzz)" number="11" sections={sections} toggle={toggle}>
            <Field label="Formule recommandee">
              <RadioGroup value={f("formule_recommandee")} onChange={(v) => set("formule_recommandee", v)} options={[
                "Formule START (2 000 EUR HT/mois)", "Formule PLUS (3 000 EUR HT/mois)",
                "Formule SMART (5 000 EUR HT/mois)", "Formule BLITZ (0 EUR + partage benefices)"
              ]} columns={2} />
            </Field>
            <h3 className="text-sm font-bold mt-4" style={{ color: "#f7c935" }}>Score de Priorite</h3>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { key: "score_urgence", label: "Urgence" },
                { key: "score_potentiel", label: "Potentiel redressement" },
                { key: "score_investissement", label: "Capacite investissement" },
                { key: "score_engagement", label: "Engagement dirigeant" },
              ].map(({ key, label }) => (
                <Field key={key} label={label}>
                  <RadioGroup value={f(key)} onChange={(v) => set(key, v)} options={["1", "2", "3", "4", "5"]} columns={5} />
                </Field>
              ))}
            </div>
            <Field label="Commentaires internes"><Textarea value={f("commentaires_internes")} onChange={(e) => set("commentaires_internes", e.target.value)} rows={3} className={textCls} /></Field>
            <Field label="Prochaines etapes">
              <CheckboxGroup value={fArr("prochaines_etapes")} onChange={(v) => set("prochaines_etapes", v)} options={[
                "Rendez-vous diagnostic approfondi", "Audit sur site", "Proposition commerciale detaillee",
                "Demarrage immediat", "Mise en attente"
              ]} columns={2} />
            </Field>
          </Section>
        )}

        {/* Save */}
        <div className="flex justify-end pt-4 pb-8">
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold text-base px-8 py-3 h-auto" data-testid="save-fiche-bottom-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
            Enregistrer la fiche
          </Button>
        </div>
      </div>
    </div>
  );
}
